import React, { FC, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import InputBase from "@mui/material/InputBase";
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

import DataTable, { createDataRow, HeadCell } from "@/components/DataTable";
import ActionMenu from "@/components/ActionMenu";
import GenericAlertModal from "@/components/AlertModal";
import Select from "@/components/Select";

import useDebounce from "@/hooks/useDebounce";
import { usePageStore } from "@/hooks/usePageStore";
import { useUserStore } from "@/hooks/useUserStore";
import {
  useAuditLogFirstRender,
  useCreateAuditlog,
} from "@/hooks/useAuditLogs";
import { useSettingsCatalogList } from "@/hooks/useSettingCatalog";
import {
  useAccountSettings,
  useArchiveAccountSetting,
  useSetAccountSettingValue,
} from "@/hooks/useAccountSettings";

import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { useAppTheme } from "@/utils/theme";
import { settingValueToDisplay } from "@/utils/settingValue";

import {
  SETTING_APPLICATIONS,
  Setting,
} from "@/domain/settingCatalog.interface";
import { AccountSetting } from "@/domain/accountSetting.interface";
import { ErrorResponse } from "@/domain/error.interface";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";

import AccountSettingValueModal from "@/ui/modals/accountSettings/AccountSettingValueModal";

const DEFAULT_PAGE_SIZE = 10;

const AccountSettingsPage: FC = () => {
  const theme = useAppTheme();
  const { showSnackBar } = useFeedBack();
  const { userInfo } = useUserStore();
  const { setPageTitle } = usePageStore();

  const [application, setApplication] = useState("backoffice");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce({ value: search });
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [valueModal, setValueModal] = useState<{
    open: boolean;
    setting?: Setting;
    override?: AccountSetting;
  }>({ open: false });

  const [clearAlert, setClearAlert] = useState<{
    open: boolean;
    setting?: Setting;
    override?: AccountSetting;
  }>({ open: false });

  const { settingsList, isLoadingSettingsList, isFetchingSettingsList } =
    useSettingsCatalogList(application || undefined);

  const {
    accountSettings,
    isLoadingAccountSettings,
    isFetchingAccountSettings,
    refetchAccountSettings,
  } = useAccountSettings();

  const setValue = useSetAccountSettingValue();
  const archiveAccountSetting = useArchiveAccountSetting();
  const createAuditLog = useCreateAuditlog();

  useEffect(() => {
    setPageTitle("Account settings");
  }, []);

  useAuditLogFirstRender({
    appType: "WEB_BACK_OFFICE",
    module: MODULE_NAME.SETTINGS,
    option: OPTIONS.ACCOUNT_SETTINGS_LIST,
    actionCode: ACTION_CODE.WEB_ACCT_SETTING_LIST_VIEW,
    action: "When entering",
    detail: "Viewed the account settings",
    transactionDate: new Date(),
    accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
    createdBy: userInfo?._id as string,
    programId: userInfo?.programId as string,
    orgUnitId: userInfo?.orgUnitIds?.[0] as string,
  });

  // Reset to the first page whenever the visible set changes.
  useEffect(() => {
    setPageNumber(0);
  }, [debouncedSearch, application]);

  // Override (AccountSetting) indexed by the setting it belongs to.
  const overrideBySettingId = useMemo(() => {
    const map = new Map<string, AccountSetting>();
    (accountSettings || []).forEach((as) => {
      if (as.settingId) map.set(as.settingId, as);
    });
    return map;
  }, [accountSettings]);

  const filteredSettings = useMemo(() => {
    const list = settingsList || [];
    const term = debouncedSearch.trim().toLowerCase();
    if (!term) return list;
    return list.filter(
      (s) =>
        s.name.toLowerCase().includes(term) ||
        (s.description || "").toLowerCase().includes(term)
    );
  }, [settingsList, debouncedSearch]);

  const pagedSettings = useMemo(
    () =>
      filteredSettings.slice(
        pageNumber * pageSize,
        (pageNumber + 1) * pageSize
      ),
    [filteredSettings, pageNumber, pageSize]
  );

  const handleOpenEdit = (setting: Setting) => {
    setValueModal({
      open: true,
      setting,
      override: overrideBySettingId.get(setting._id),
    });
  };

  const handleCloseModal = () => {
    setValueModal({ open: false });
  };

  const handleSaveValue = async (value: unknown) => {
    const setting = valueModal.setting;
    if (!setting) return;

    const currentUserId = userInfo?._id as string;
    try {
      await setValue.mutateAsync({
        settingId: setting._id,
        payload: {
          value,
          // First write for this account/setting creates the override; later
          // writes update it.
          ...(valueModal.override
            ? { updatedBy: currentUserId }
            : { createdBy: currentUserId }),
        },
      });

      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.SETTINGS,
        option: OPTIONS.ACCOUNT_SETTING_VALUE,
        actionCode: ACTION_CODE.WEB_ACCT_SETTING_SET,
        action: "When setting an account value",
        detail: `Set value for setting ${setting.name}`,
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
      });

      showSnackBar("The value has been saved.", "success");
      handleCloseModal();
      refetchAccountSettings();
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred"
      );
    }
  };

  const handleConfirmClear = async () => {
    const override = clearAlert.override;
    const setting = clearAlert.setting;
    if (!override || !setting) return;

    try {
      await archiveAccountSetting.mutateAsync(override._id);

      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.SETTINGS,
        option: OPTIONS.ACCOUNT_SETTING_VALUE,
        actionCode: ACTION_CODE.WEB_ACCT_SETTING_CLEAR,
        action: "When clearing an account value",
        detail: `Cleared override for setting ${setting.name}`,
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
      });

      showSnackBar("The override has been cleared.", "success");
      setClearAlert({ open: false });
      refetchAccountSettings();
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred"
      );
    }
  };

  const headCells: HeadCell[] = [
    {
      id: "name",
      isSortable: false,
      disablePadding: true,
      label: "Name",
      align: "left",
      width: "200px",
    },
    {
      id: "module",
      isSortable: false,
      disablePadding: false,
      label: "Module / Section",
      align: "left",
      width: "180px",
    },
    {
      id: "type",
      isSortable: false,
      disablePadding: false,
      label: "Type",
      align: "left",
      width: "100px",
    },
    {
      id: "value",
      isSortable: false,
      disablePadding: false,
      label: "Effective value",
      align: "left",
    },
    {
      id: "source",
      isSortable: false,
      disablePadding: false,
      label: "Source",
      align: "left",
      width: "120px",
    },
    {
      id: "action",
      isSortable: false,
      disablePadding: false,
      label: "Actions",
      align: "right",
      width: "100px",
    },
  ];

  const rows = useMemo(() => {
    return pagedSettings.map((setting, index) => {
      const override = overrideBySettingId.get(setting._id);
      const hasOverride = !!override;
      const effectiveValue = hasOverride ? override?.value : setting.default;

      const actionMenu = [
        {
          value: "Set value",
          onClick: () => handleOpenEdit(setting),
          icon: <EditIcon fontSize="small" />,
        },
        {
          value: "Clear override",
          onClick: () => setClearAlert({ open: true, setting, override }),
          icon: <RestartAltIcon fontSize="small" />,
          disabled: !hasOverride,
          sx: { color: theme.palette.error.main },
        },
      ];

      return createDataRow(index, {
        name: <div>{setting.name}</div>,
        module: (
          <div>
            {setting.module}
            {setting.section ? ` / ${setting.section}` : ""}
          </div>
        ),
        type: <div>{setting.type}</div>,
        value: (
          <div className="max-w-[280px] truncate">
            {settingValueToDisplay(setting.type, effectiveValue)}
          </div>
        ),
        source: (
          <Chip
            sx={{
              bgcolor: hasOverride
                ? theme.palette.brand.light
                : theme.palette.surface.light2,
              borderWidth: 1,
              borderStyle: "solid",
              borderColor: hasOverride
                ? theme.palette.brand.main
                : theme.palette.outline.dark,
            }}
            label={
              <div className="text-xs">
                {hasOverride ? "Override" : "Default"}
              </div>
            }
          />
        ),
        action: <ActionMenu id={setting._id} options={actionMenu} />,
      });
    });
  }, [pagedSettings, overrideBySettingId, theme]);

  const isLoading =
    isLoadingSettingsList ||
    isFetchingSettingsList ||
    isLoadingAccountSettings ||
    isFetchingAccountSettings;

  return (
    <>
      <div className="w-full overflow-y-auto scroll-smooth">
        <div className="full-width-container mx-auto">
          <div className="flex flex-col items-center justify-start min-h-screen space-y-5">
            <Card sx={{ width: "100%", borderRadius: "8px" }}>
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  rowGap: "20px",
                }}
              >
                <Typography variant="headlineSmall" component="h1">
                  Account settings
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    gap: "0.5rem",
                    width: "100%",
                    flexDirection: { xs: "column", md: "row" },
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: theme.palette.surface.light2,
                      px: 2,
                      width: "100%",
                    }}
                    className="flex rounded-lg py-3 max-w-full items-center justify-between h-14"
                  >
                    <InputBase
                      className="flex-1"
                      placeholder="Search setting"
                      inputProps={{ "aria-label": "search" }}
                      onChange={(e) => setSearch(e.target.value)}
                      value={search}
                    />
                    <SearchIcon className="text-icon-primary" />
                  </Box>
                  <Select
                    handleChange={(value) =>
                      setApplication((value as string) || "")
                    }
                    data={SETTING_APPLICATIONS.map((a) => ({
                      key: a,
                      value: a,
                    }))}
                    value={application}
                    label="Application"
                    id="account-setting-application-filter"
                    maxWidth={240}
                    showClearOption
                    fullWidth
                  />
                </Box>

                <DataTable
                  data={rows}
                  headCells={headCells}
                  order="asc"
                  orderBy="name"
                  rowsPerPage={pageSize}
                  page={pageNumber}
                  total={filteredSettings.length}
                  isLoading={isLoading}
                  messageEmptyData="No settings to show."
                  onChangePage={(_event, newPage) => setPageNumber(newPage)}
                  onChangeSize={(event) => {
                    setPageNumber(0);
                    setPageSize(Number(event.target.value));
                  }}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {valueModal.open && valueModal.setting && (
        <AccountSettingValueModal
          open={valueModal.open}
          onClose={handleCloseModal}
          setting={valueModal.setting}
          currentValue={
            valueModal.override
              ? valueModal.override.value
              : valueModal.setting.default
          }
          hasOverride={!!valueModal.override}
          onSave={handleSaveValue}
          isPending={setValue.isPending}
        />
      )}

      {clearAlert.open && (
        <GenericAlertModal
          onCancel={() => setClearAlert({ open: false })}
          onClick={handleConfirmClear}
          title="Clear override"
          description="Reset this setting to the catalog default for this account?"
          submitText="Confirm"
          cancelText="Cancel"
          isLoading={archiveAccountSetting.isPending}
          theme={theme}
        />
      )}
    </>
  );
};

export default AccountSettingsPage;
